import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FaUsers } from "react-icons/fa6";
import { BiMessage, BiSolidReport, BiStats, BiTask } from "react-icons/bi";
import { IoChatbubblesOutline, IoSettingsOutline, IoLogOutOutline, IoMoonOutline, IoSunnyOutline } from "react-icons/io5";
import { getFlavorConfig } from "../config/flavorConfig";
import { firebaseAuth } from "../../context/Firebase";
import { signOut } from "firebase/auth";


const Sidebar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const flavor = getFlavorConfig();
  const navigate = useNavigate();

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleLogout = async () => {
    await signOut(firebaseAuth);
    navigate("/");
  };

  const navItemClass = ({ isActive }) =>
    `flex items-center p-3 rounded-lg transition-colors duration-200 ${
      isActive ? "font-semibold sidebar-nav-active" : "hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-700 dark:text-zinc-400"
    }`;

  const navItemStyle = ({ isActive }) =>
    isActive
      ? {
          backgroundColor: "var(--app-active-bg)",
          color: "var(--app-active-text)",
        }
      : undefined;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      {/* Toggle button for mobile */}
    <button
  className="fixed z-50 flex items-center justify-center w-10 h-10 text-gray-700 bg-white/80 backdrop-blur-md rounded-md top-4 left-4 lg:hidden hover:bg-white hover:shadow"
  onClick={toggleMenu}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
</button>



      {/* Backdrop for mobile sidebar */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={toggleMenu}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`sidebar fixed top-0 left-0 h-screen w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-40 flex flex-col ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        style={{ backgroundColor: "var(--app-sidebar-bg)" }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-6 text-xl font-bold text-gray-800 dark:text-zinc-100 border-b border-gray-200 dark:border-zinc-800 lg:pl-6 pl-14">
          <img src={flavor.logoSrc} alt={flavor.appName} className="w-8 h-8 rounded-md" />
          <span>{flavor.appName}</span>
        </div>

        {/* Menu Items — scrollable so logout is never pushed off screen */}
        <nav className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-1">
          <NavLink to="/admin" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <FaUsers className="mr-3 text-xl" />
            <span className="flex-1">User List</span>
          </NavLink>
          <NavLink to="/post" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <BiMessage className="mr-3 text-xl" />
            <span className="flex-1">Post</span>
          </NavLink>
          <NavLink to="/jobs" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <BiSolidReport className="mr-3 text-xl" />
            <span className="flex-1">Quick Reads</span>
          </NavLink>
          <NavLink to="/donation" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <BiStats className="mr-3 text-xl" />
            <span className="flex-1">Donation</span>
          </NavLink>
          <NavLink to="/bio" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <BiTask className="mr-3 text-xl" />
            <span className="flex-1">Marriage Profiles</span>
          </NavLink>
          <NavLink to="/improved-chat" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <IoChatbubblesOutline className="mr-3 text-xl" />
            <span className="flex-1">Chats</span>
          </NavLink>
          <NavLink to="/mass-messaging" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <IoChatbubblesOutline className="mr-3 text-xl" />
            <span className="flex-1">Messaging</span>
          </NavLink>
          <NavLink to="/settings" className={navItemClass} style={navItemStyle} onClick={() => setIsMenuOpen(false)}>
            <IoSettingsOutline className="mr-3 text-xl" />
            <span className="flex-1">Settings</span>
          </NavLink>
        </nav>

        {/* Footer — always pinned at bottom */}
        <div className="border-t border-gray-200 dark:border-zinc-800 px-4 py-3 shrink-0">
          {/* Dark Mode Toggle */}
          <div className="flex items-center justify-between p-3 mb-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors duration-200">
            <div className="flex items-center gap-2">
              {isDarkMode ? (
                <IoMoonOutline className="text-lg text-gray-700 dark:text-zinc-400" />
              ) : (
                <IoSunnyOutline className="text-lg text-gray-700 dark:text-zinc-400" />
              )}
              <span className="text-sm text-gray-700 dark:text-zinc-400">
                {isDarkMode ? 'Dark Mode' : 'Light Mode'}
              </span>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-label="Toggle dark mode"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center w-full p-3 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-300"
          >
            <IoLogOutOutline className="mr-3 text-xl" />
            <span className="flex-1 text-left">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;